// src/lib/posthog/link-events.ts
//
// Pure decision rules for the landing-page link events (`cta_click`,
// `outbound_click`). No `window`, no React, no `capture` call: the one
// delegated listener (`landing-link-analytics.tsx`) owns the wiring and calls
// these functions with plain values, so the decisions stay testable with
// literal tables — this repo's Vitest config runs with `environment: "node"`
// (no jsdom), so nothing inside a `.tsx` is reachable by a test.

export type CtaPlacement =
  | "hero_primary"
  | "hero_secondary"
  | "header_sign_in"
  | "header_primary"
  | "sticky_docs"
  | "sticky_primary";

const CTA_PLACEMENTS: ReadonlySet<string> = new Set<CtaPlacement>([
  "hero_primary",
  "hero_secondary",
  "header_sign_in",
  "header_primary",
  "sticky_docs",
  "sticky_primary",
]);

export function isCtaPlacement(value: string | null | undefined): value is CtaPlacement {
  return typeof value === "string" && CTA_PLACEMENTS.has(value);
}

/** Trimmed, whitespace-collapsed, cut at 80 chars. "" when there is no text. */
export function linkText(raw: string | null | undefined): string {
  if (raw == null) return "";
  return raw.trim().replace(/\s+/g, " ").slice(0, 80);
}

/**
 * Same trim/collapse/80-char cut as `linkText`, but `undefined` when there is
 * no text (absent or blank) so the caller can omit `cta_label` from the
 * payload instead of sending an empty string.
 */
export function ctaLabel(raw: string | null | undefined): string | undefined {
  const text = linkText(raw);
  return text === "" ? undefined : text;
}

/**
 * The destination HOST only, never the path, query or fragment. A
 * `mailto:`/`tel:` link reports the scheme alone — the address itself never
 * travels. A relative href, an absolute href whose host equals `currentHost`
 * (case-insensitively), or anything that fails to parse is not an outbound
 * click and returns `null`. Never throws.
 */
export function outboundHost(href: string, currentHost: string): string | null {
  if (typeof href !== "string" || href.length === 0) return null;
  const trimmed = href.trim();
  if (/^mailto:/i.test(trimmed)) return "mailto";
  if (/^tel:/i.test(trimmed)) return "tel";
  let url: URL;
  try {
    url = new URL(trimmed);
  } catch {
    return null;
  }
  if (url.host === "" || url.host.toLowerCase() === currentHost.toLowerCase()) return null;
  return url.host;
}

/**
 * The href as written for an internal path, the host alone for an
 * absolute URL. Never throws.
 */
export function ctaDestination(href: string): string {
  if (typeof href !== "string") return "";
  try {
    const url = new URL(href);
    return url.host !== "" ? url.host : href;
  } catch {
    return href;
  }
}
